import React, { useEffect, useState, useRef } from 'react';
import useArrowNav from '../hooks/useArrowNav';
import { Users, ClipboardList, FlaskConical, Calendar, Clock, Activity } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import { writeParams } from '../lib/urlState';

/**
 * Map an audit_log row's resource_type to a page id understood by
 * onNavigate. Audit-log rows come from many subsystems with slightly
 * different vocabularies (patient vs. patients vs. Patient_Record);
 * we normalize to lowercase and accept the common variants. Anything
 * unrecognized falls back to the Activity Hub, which is the unified
 * inbox introduced in the operations-section consolidation.
 */
function activityNavTarget(item) {
  const type = (item && item.resource_type ? String(item.resource_type) : '').toLowerCase();
  switch (type) {
    case 'patient':
    case 'patients':
    case 'patient_record':
      return 'patients';
    case 'encounter':
    case 'encounters':
      return 'encounters';
    case 'document':
    case 'documents':
      return 'documents';
    case 'lab':
    case 'labs':
    case 'lab_result':
    case 'lab_results':
      return 'labs';
    default:
      return 'activity';
  }
}

/**
 * Format an ISO timestamp as a short relative-time string. Avoids
 * pulling in a date library for what's basically four conditionals.
 * Caller passes through the raw audit_log.timestamp value.
 */
function formatRelativeTime(timestamp) {
  if (!timestamp) return '';
  const now = Date.now();
  const ts = new Date(timestamp).getTime();
  if (Number.isNaN(ts)) return '';
  const diffSec = Math.max(0, Math.floor((now - ts) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function Dashboard({ user, onNavigate }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const statsGridRef = useRef(null);
  const { onKeyDown: handleStatsKeyDown } = useArrowNav(statsGridRef, 'button.stat-card', { vertical: false, activateOnFocus: false });

  useEffect(() => {
    async function loadStats() {
      try {
        const data = await window.zignagenixAPI.getDashboardStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, []);

  if (loading) return <div className="page-loading">Loading dashboard...</div>;

  const displayName = user?.displayName || user?.name || 'User';

  return (
    <div className="page">
      {/* Header Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#1E293B', margin: '0 0 8px 0' }}>
            Good morning, {displayName}
          </h1>
          <p style={{ fontSize: '14px', color: '#64748B', margin: 0 }}>Here's what's happening today</p>
        </div>
        <PrintButton pageTitle="Dashboard" subtitle="Platform Overview" />
      </div>

      {/* KPI Stat Cards Grid */}
      <div className="stats-grid" ref={statsGridRef} onKeyDown={handleStatsKeyDown} role="group" aria-label="Quick navigation cards">
        <button
          className="stat-card"
          onClick={() => onNavigate('patients')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon stat-icon--blue" style={{
            width: 56,
            height: 56,
            background: 'rgba(32, 178, 170, 0.12)',
            borderRadius: '50%'
          }}>
            <Users size={28} style={{ color: '#00BCD4' }} />
          </div>
          <div className="stat-info">
            <span className="stat-value" style={{ fontSize: '32px', fontWeight: 700, color: '#1E293B' }}>
              {stats?.totalPatients ?? 0}
            </span>
            <span className="stat-label" style={{ fontSize: '14px', color: '#64748B' }}>Total Patients</span>
          </div>
        </button>

        <button
          className="stat-card"
          onClick={() => onNavigate('encounters')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon stat-icon--amber" style={{
            width: 56,
            height: 56,
            background: 'rgba(242, 101, 34, 0.1)',
            borderRadius: '50%'
          }}>
            <ClipboardList size={28} style={{ color: '#F26522' }} />
          </div>
          <div className="stat-info">
            <span className="stat-value" style={{ fontSize: '32px', fontWeight: 700, color: '#1E293B' }}>
              {stats?.openEncounters ?? 0}
            </span>
            <span className="stat-label" style={{ fontSize: '14px', color: '#64748B' }}>Open Encounters</span>
          </div>
        </button>

        <button
          className="stat-card"
          onClick={() => onNavigate('labs')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon stat-icon--red" style={{
            width: 56,
            height: 56,
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '50%'
          }}>
            <FlaskConical size={28} style={{ color: '#EF4444' }} />
          </div>
          <div className="stat-info">
            <span className="stat-value" style={{ fontSize: '32px', fontWeight: 700, color: '#1E293B' }}>
              {stats?.abnormalLabs ?? 0}
            </span>
            <span className="stat-label" style={{ fontSize: '14px', color: '#64748B' }}>Abnormal Labs</span>
            {(stats?.abnormalLabs ?? 0) > 0 && (
              <span style={{ fontSize: '12px', color: '#EF4444', fontWeight: 600, marginTop: '4px' }}>Needs review</span>
            )}
          </div>
        </button>

        {/*
          ⛔⛔ THIS CARD SHOWED A HARDCODED 12 — Card P111 (B3b).
          It read `{stats?.appointments ?? 12}` while db:dashboard:stats
          (routes/admin.js) returned no `appointments` key at all, so the
          fallback was not a fallback: it was the ONLY value this card had
          ever displayed. Beneath it, a literal "+0.89%" trend. And the
          <button> carried `cursor: pointer` with NO onClick, so it invited a
          click it could not answer — reported as a dead click by two staff
          members on 23-24 Oct.

          ⚠ WHICH TABLE, AND WHY. `appointments` — the CLINICAL scheduling
          table — for one reason above all: it is the SAME table the page this
          card opens reads (routes/scheduling.js:22,42,63 → SchedulingPage).
          A tile and its destination must not disagree. The other three cards
          on this row are clinical too (patients / encounters / lab_results),
          so the row stays coherent.

          ⛔ WHAT THIS NUMBER IS NOT, and staff must be told: GoHighLevel
          appointments do NOT land in `appointments`. They land in
          `crm_appointments`, written by routes/ghl_webhooks.js:610 and
          scripts/ghl_daily_sync.js, which NOTHING on this page reads. So a
          low number here is not a sync failure and not a lost appointment —
          it is the clinical table, honestly counted. Joining the two halves
          is Card P108 item C1a and needs Daniel + Carlos; it is deliberately
          not attempted here. The empty state below carries the bridge.

          Aggregate only: a COUNT, never a name, a time or a patient.
        */}
        <button
          className="stat-card"
          onClick={() => onNavigate('scheduling')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon stat-icon--green" style={{
            width: 56,
            height: 56,
            background: 'rgba(34, 197, 94, 0.1)',
            borderRadius: '50%'
          }}>
            <Calendar size={28} style={{ color: '#22C55E' }} />
          </div>
          <div className="stat-info">
            <span className="stat-value" style={{ fontSize: '32px', fontWeight: 700, color: '#1E293B' }}>
              {stats?.appointmentsToday ?? 0}
            </span>
            <span className="stat-label" style={{ fontSize: '14px', color: '#64748B' }}>Appointments Today</span>
            <span style={{ fontSize: '12px', color: '#94A3B8', fontWeight: 600, marginTop: '4px' }}>Clinical schedule</span>
          </div>
        </button>
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginTop: '32px' }}>
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Patient Overview Chart */}
          <div style={{
            background: '#FFFFFF',
            border: '1px solid #E8ECF1',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#1E293B' }}>
              Patient Overview
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '12px', color: '#94A3B8' }}>by Age Groups</p>
            <div style={{
              height: '200px',
              background: '#F5F6FA',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94A3B8',
              fontSize: '14px'
            }}>
              Bar Chart Placeholder
            </div>
          </div>

          {/* Revenue Chart */}
          <div style={{
            background: '#FFFFFF',
            border: '1px solid #E8ECF1',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#1E293B' }}>
              Revenue
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '12px', color: '#94A3B8' }}>Monthly trend</p>
            <div style={{
              height: '200px',
              background: '#F5F6FA',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94A3B8',
              fontSize: '14px'
            }}>
              Line Chart Placeholder
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Calendar Widget */}
          <div style={{
            background: '#FFFFFF',
            border: '1px solid #E8ECF1',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#1E293B' }}>
              Calendar
            </h3>
            <div style={{
              height: '200px',
              background: '#F5F6FA',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94A3B8',
              fontSize: '14px'
            }}>
              Calendar Widget
            </div>
          </div>

          {/* Today's Schedule */}
          <div style={{
            background: '#FFFFFF',
            border: '1px solid #E8ECF1',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#1E293B' }}>
              Today's Schedule
            </h3>
            {/*
              ⛔⛔ THIS PANEL SHOWED THREE INVENTED PATIENTS — Card P111 (B3a).
              THE MOST URGENT ITEM IN THE WHOLE P108 TRIAGE, and not because it
              crashed. It never crashed. It rendered, in production, on a
              clinical dashboard, a hardcoded array:

                  09:00 AM  <name> - Follow-up
                  10:30 AM  <name> - Consultation
                  02:00 PM  <name> - Lab Review

              Three fictional people with appointment times and visit types,
              presented as today's schedule to doctors and Patient Experience
              Managers. No test failed. No log fired. It shipped for months,
              and it is PRECISELY the failure mode a passing suite cannot see:
              a literal is indistinguishable from a fetched row once rendered.

              ⚠ WHY AN EMPTY STATE AND NOT A REAL LIST. Building the real list
              means answering "which appointments?" — and that question has two
              answers today (`appointments`, clinical; `crm_appointments`,
              GHL-fed) which nothing joins. Choosing one here would quietly
              decide Card P108 item C1a, a call that belongs to Daniel and
              Carlos. An honest empty state is the correct interim: it tells
              the truth NOW and it does not pre-empt their decision. The real
              list is proposed in this PR's body.

              ⚠ THE LINK IS THE BRIDGE. Clinical and GHL-synced appointments
              live in different tables; the CRM Calendar is where the
              GHL-synced ones are, so someone who expected to see appointments
              here is one click from the place they actually are. `tab` is an
              allowlisted slug in src/lib/urlState.js and CRMPage reads it
              through readParam on mount — no new URL surface, and no id or
              name ever enters the address bar.
            */}
            <div style={{
              padding: '20px 16px',
              background: '#F8FAFC',
              border: '1px dashed #CBD5E1',
              borderRadius: '12px',
              textAlign: 'center',
            }}>
              <Clock size={20} style={{ color: '#94A3B8' }} />
              <p style={{ margin: '8px 0 4px 0', fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                No clinical appointments scheduled for today
              </p>
              <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#94A3B8' }}>
                GoHighLevel-synced appointments are shown on the CRM Calendar.
              </p>
              <button
                type="button"
                onClick={() => { writeParams({ tab: 'calendar' }); onNavigate('crm'); }}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#00BCD4', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Open CRM Calendar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{
        marginTop: '32px',
        background: '#FFFFFF',
        border: '1px solid #E8ECF1',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)'
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#1E293B' }}>
          <Activity size={18} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
          Recent Activity
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {(() => {
            const items = Array.isArray(stats?.recentActivity) ? stats.recentActivity : [];
            if (items.length === 0) {
              return (
                <p style={{
                  margin: 0,
                  padding: '12px',
                  fontSize: '13px',
                  color: '#94A3B8',
                  fontStyle: 'italic'
                }}>
                  No recent activity.
                </p>
              );
            }
            return items.map((item, idx) => {
              // Build the subtitle from whatever fields the audit_log row
              // actually has. user_name comes from the LEFT JOIN; details
              // may be null. Fall back to resource_type + resource_id so
              // the row never renders empty.
              const subtitleParts = [item.user_name, item.details].filter(Boolean);
              const subtitle = subtitleParts.length > 0
                ? subtitleParts.join(' — ')
                : (item.resource_type
                    ? `${item.resource_type}${item.resource_id ? ' #' + item.resource_id : ''}`
                    : '');
              const target = activityNavTarget(item);
              return (
                <button
                  key={item.id ?? idx}
                  type="button"
                  onClick={() => onNavigate(target)}
                  title={`Open ${target}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    borderBottom: idx < items.length - 1 ? '1px solid #E8ECF1' : 'none',
                    border: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderTop: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    transition: 'background 0.12s ease'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onFocus={(e)      => { e.currentTarget.style.background = '#F1F5F9'; }}
                  onBlur={(e)       => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 600, color: '#00BCD4' }}>
                      {item.action || 'Activity'}
                    </p>
                    <p style={{
                      margin: 0,
                      fontSize: '12px',
                      color: '#64748B',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {subtitle}
                    </p>
                  </div>
                  <span style={{ fontSize: '12px', color: '#94A3B8', whiteSpace: 'nowrap', marginLeft: '16px' }}>
                    {formatRelativeTime(item.timestamp)}
                  </span>
                </button>
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
